import React from "react";
import { PurchaseOrderService } from "src/services";
import { DD_MM_YYYY, RoleHelper } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { PurchaseOrderColumnHeader, PurchaseOrderFileType, VendorFileType } from "src/enums";
import { PurchaseOrder, TableColumn, UpdatePurchaseOrderOpsNoteParams } from "src/types";
import { useAuthorisation } from "src/context";
import { TrackingLink, EditOpsNote, SendPurchaseOrderBtn } from "src/components";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import {
  Truck,
  FileText,
  Info,
  Edit,
  Receipt,
  ReceiptText,
  Download,
  FilePlus,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from "lucide-react";
import { Button } from "src/shadcn/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "src/shadcn/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { Link } from "react-router-dom";
import { PurchaseOrderFulfilmentCompletionCell } from "./shared/purchaseOrderFulfilmentCompletionCell";
import { formatEstimatedDeliveryDate } from "./shared/dateFormatters";

interface PurchaseOrdersTableDashProps {
  purchaseOrders: PurchaseOrder[];
  columnsToHide?: PurchaseOrderColumnHeader[];
  onUpdateOpsNote: (params: UpdatePurchaseOrderOpsNoteParams) => void;
  downloadFile: (purchaseOrderId: string, purchaseOrderFileType: PurchaseOrderFileType) => void;
  isFileDownloading?: boolean;
  additionalActions?: (row: PurchaseOrder) => React.ReactNode;
}

const PurchaseOrdersDashTable: React.FC<PurchaseOrdersTableDashProps> = ({
  purchaseOrders,
  columnsToHide,
  onUpdateOpsNote,
  downloadFile,
  isFileDownloading,
  additionalActions
}) => {
  const { userRole } = useAuthorisation();

  const purchaseOrdersTableColumns: TableColumn<PurchaseOrderColumnHeader, PurchaseOrder>[] = [
    {
      header: PurchaseOrderColumnHeader.PURCHASE_ORDER,
      column: (row: PurchaseOrder) => (
        <Link
          to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
          className="text-primary-900 hover:text-primary-700 hover:underline transition-colors"
        >
          {row.id}
        </Link>
      ),
      sticky: true
    },
    {
      header: PurchaseOrderColumnHeader.SUB_PURCHASE_ORDERS,
      column: (row: PurchaseOrder) => (
        <div className="flex flex-col gap-1 items-center">
          {row.ukSubPurchaseOrderId && (
            <Link
              to={`${ROUTES_CONFIG.subPurchaseOrder.path}?id=${row.ukSubPurchaseOrderId}`}
              className="text-primary-900 hover:text-primary-700 hover:underline transition-colors whitespace-nowrap"
            >
              {row.ukSubPurchaseOrderId}
            </Link>
          )}
          {row.euSubPurchaseOrderId && (
            <Link
              to={`${ROUTES_CONFIG.subPurchaseOrder.path}?id=${row.euSubPurchaseOrderId}`}
              className="text-primary-900 hover:text-primary-700 hover:underline transition-colors whitespace-nowrap"
            >
              {row.euSubPurchaseOrderId}
            </Link>
          )}
        </div>
      ),
      roleRequired: ["HEALF_OPERATIONS_TEAM", "WAREHOUSE"],
      styles: { textAlign: "center", minWidth: "172px" }
    },
    {
      header: PurchaseOrderColumnHeader.CONFIRMATION_STATUS,
      column: (row: PurchaseOrder) => {
        if (!row.confirmationStatus) return undefined;
        const isInternalUser = userRole !== "WAREHOUSE" && userRole !== "RELABELLING" && userRole !== "VENDOR";
        const showAaError = !!row.aaError && isInternalUser;
        const showGxoError = !!row.gxoError && userRole !== "WAREHOUSE" && userRole !== "RELABELLING";
        const status = showAaError ? "AA Error" : showGxoError ? "GXO Error" : row.confirmationStatus;
        const linkPath = showAaError
          ? `${ROUTES_CONFIG.resolveAAError.path}?id=${row.id}`
          : showGxoError
            ? `${ROUTES_CONFIG.resolveGXOError.path}?id=${row.id}`
            : RoleHelper.isHealfOperationsTeam(userRole) && row.confirmationStatus === "In Review"
              ? `${ROUTES_CONFIG.reviewPurchaseOrderConfirmation.path}?id=${row.id}`
              : RoleHelper.isHealfOperationsTeam(userRole) &&
                (row.confirmationStatus === "TBC" || row.confirmationStatus === "Overdue")
                ? `${ROUTES_CONFIG.updatePurchaseOrderConfirmation.path}?id=${row.id}`
                : undefined;

        return (
          <span className="flex justify-center">
            <StatusPill status={status} linkPath={linkPath} />
          </span>
        );
      },
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.TRACKING_NUMBER,
      column: (row: PurchaseOrder) =>
        row.trackingNo ? (
          <div className="flex justify-center">
            <TrackingLink trackingNo={row.trackingNo} carrier={row.carrier} collectionOrder={row.collectionOrder} />
          </div>
        ) : (
          row.trackingNo
        ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.CARRIER_STATUS,
      column: (row: PurchaseOrder) => row.carrierStatus,
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.CHECK_IN_STATUS,
      column: (row: PurchaseOrder) => (
        <span className="flex justify-center">
          {row.checkInStatus && (
            <StatusPill
              status={row.checkInStatus}
              linkPath={
                row.checkInFlaggedIssues?.length && userRole !== "WAREHOUSE" && userRole !== "RELABELLING"
                  ? `${ROUTES_CONFIG.receivingDiscrepancies.path}?id=${row.id}`
                  : undefined
              }
            />
          )}
        </span>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.COMPLETION_STATUS,
      column: (row: PurchaseOrder) => {
        const isChildConfirmationPending = row.completionStatus === "Draft" && row.isChildConfirmationPending === true;
        const scheduledApprovalStatus =
          row.completionStatus === "Scheduled" ? row.scheduledPOApprovalStatus : undefined;
        const ScheduledApprovalIcon =
          scheduledApprovalStatus === "Pending"
            ? Clock
            : scheduledApprovalStatus === "Approved"
              ? CheckCircle2
              : scheduledApprovalStatus === "Rejected"
                ? XCircle
                : null;
        const scheduledApprovalLabel =
          scheduledApprovalStatus === "Pending"
            ? "Pending"
            : scheduledApprovalStatus === "Approved"
              ? "Approved"
              : scheduledApprovalStatus === "Rejected"
                ? "Rejected"
                : null;
        const scheduledApprovalIconClass =
          scheduledApprovalStatus === "Pending"
            ? "text-amber-500"
            : scheduledApprovalStatus === "Approved"
              ? "text-emerald-500"
              : scheduledApprovalStatus === "Rejected"
                ? "text-red-500"
                : "";
        return (
          <span className="flex items-center justify-center gap-1.5">
            {isChildConfirmationPending ? (
              <StatusPill
                status="Pending"
                linkPath={
                  RoleHelper.isHealfOperationsTeam(userRole)
                    ? `${ROUTES_CONFIG.confirmChildPurchaseOrder.path}?id=${row.id}`
                    : undefined
                }
              />
            ) : (
              <StatusPill status={row.completionStatus} />
            )}
            {ScheduledApprovalIcon && scheduledApprovalLabel && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex shrink-0" aria-label={scheduledApprovalLabel}>
                      <ScheduledApprovalIcon className={`h-4 w-4 ${scheduledApprovalIconClass}`} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{scheduledApprovalLabel}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!isChildConfirmationPending &&
              (row.completionStatus === "Draft" || row.completionStatus === "Scheduled") &&
              RoleHelper.isHealfOperationsTeam(userRole) && (
                <SendPurchaseOrderBtn id={row.id} vendorName={row.vendorName} />
              )}
          </span>
        );
      },
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.FULFILMENT_COMPLETION,
      column: (row: PurchaseOrder) => (
        <PurchaseOrderFulfilmentCompletionCell
          purchaseOrder={row}
          canViewWarehouseProgress={RoleHelper.isHealfTeam(userRole) || userRole === "WAREHOUSE"}
        />
      ),
      styles: { minWidth: "300px" }
    },
    {
      header: PurchaseOrderColumnHeader.ESTIMATED_DELIVERY_DATE,
      column: (row: PurchaseOrder) =>
        formatEstimatedDeliveryDate(
          row.carrierEstimatedDeliveryDate,
          row.confirmationDetails?.estDeliveryDate,
          row.estimatedDeliveryDate
        ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.EST_COLLECTION_DATE,
      column: (row: PurchaseOrder) =>
        row.confirmationDetails?.estCollectionDate
          ? `${DD_MM_YYYY.formatDate(new Date(row.confirmationDetails.estCollectionDate))}\u00A0(Confirmed)`
          : undefined,
      styles: { textAlign: "center" }
    },
    // {
    //   header: PurchaseOrderColumnHeader.UPDATE_SHIPMENT_DETAILS,
    //   column: (row: PurchaseOrder) =>
    //     !PurchaseOrderService.isPOShipmentDetailsLocked(row) ? (
    //       <EditPurchaseOrderBtn id={row.id} type={"UPDATE_SHIPMENT_DETAILS"} />
    //     ) : undefined,
    //   roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]
    // },
    // {
    //   header: PurchaseOrderColumnHeader.PARCEL_HERO_TIMELINE,
    //   column: (row: PurchaseOrder) =>
    //     PurchaseOrderService.isPOParcelHeroEligible(row) ? (
    //       <PageLinkButton path={`${ROUTES_CONFIG.parcelHeroTimeline.path}?id=${row.id}`} />
    //     ) : undefined,
    //   roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]
    // },
    {
      header: PurchaseOrderColumnHeader.VENDOR,
      column: (row: PurchaseOrder) => row.vendorName
    },
    {
      header: PurchaseOrderColumnHeader.ORIGIN,
      column: (row: PurchaseOrder) => row.origin,
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.ASN,
      column: (row: PurchaseOrder) => row.asn,
      styles: { whiteSpace: "nowrap", textAlign: "center" }
    },
    // {
    //   header: PurchaseOrderColumnHeader.PURCHASE_ORDER_PDF,
    //   column: (row: PurchaseOrder) =>
    //     PurchaseOrderService.hasPurchaseOrderPDFBeenSent(row) ? (
    //       <div style={{ position: "relative", display: "inline-block" }}>
    //         <FileTypeBadge fileType={row.fileType || VendorFileType.PDF} />
    //         <FileDownloadButton
    //           handleDownload={() => downloadFile(row.id, PurchaseOrderFileType.PDF)}
    //           isDisabled={isFileDownloading}
    //         />
    //       </div>
    //     ) : undefined
    // },
    // {
    //   header: PurchaseOrderColumnHeader.MORE_INFO,
    //   column: (row: PurchaseOrder) => <PageLinkButton path={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`} />
    // },
    // {
    //   header: PurchaseOrderColumnHeader.AMEND,
    //   column: (row: PurchaseOrder) =>
    //     !PurchaseOrderService.isPOAmendLocked(row) ? <EditPurchaseOrderBtn id={row.id} type={"AMEND"} /> : undefined,
    //   roleRequired: ["HEALF_OPERATIONS_TEAM"]
    // },
    // {
    //   header: "CHILD_PO" as any,
    //   column: (row: PurchaseOrder) =>
    //     row.confirmationStatus === "Confirmed" &&
    //     (RoleHelper.isHealfOperationsTeam(userRole) || userRole === "WAREHOUSE") ? (
    //       <Link
    //         to={`${ROUTES_CONFIG.createChildPurchaseOrder.path}?id=${row.id}`}
    //         className="btn--text"
    //         style={{ display: "flex", alignItems: "center", gap: "4px", textAlign: "left" }}
    //       >
    //         <PlusIcon className="h-4 w-4 text-muted-600" />
    //         Create
    //       </Link>
    //     ) : undefined,
    //   roleRequired: ["HEALF_OPERATIONS_TEAM", "WAREHOUSE"]
    // },
    {
      header: PurchaseOrderColumnHeader.COST,
      column: (row: PurchaseOrder) => formatFloatWithTwoDecimals(row.totalCost),
      roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"],
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.HMRC_COST,
      column: (row: PurchaseOrder) => (row.hmrcTotalCost ? formatFloatWithTwoDecimals(row.hmrcTotalCost) : undefined),
      roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"],
      styles: { textAlign: "center" }
    },
    // {
    //   header: PurchaseOrderColumnHeader.INVOICE,
    //   column: (row: PurchaseOrder) =>
    //     row.hasInvoice ? (
    //       <FileDownloadButton
    //         handleDownload={() => downloadFile(row.id, PurchaseOrderFileType.INVOICES)}
    //         isDisabled={isFileDownloading}
    //       />
    //     ) : userRole === "HEALF_ACCOUNTANT" ||
    //       userRole === "HEALF_OPERATIONS" ||
    //       userRole === "HEALF_OPERATIONS_MANAGER" ? (
    //       <EditPurchaseOrderBtn id={row.id} type={"UPDATE_INVOICE"} />
    //     ) : undefined,
    //   roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]
    // },
    {
      header: PurchaseOrderColumnHeader.SENDER_EMAIL,
      column: (row: PurchaseOrder) => row.senderEmail
    },
    {
      header: PurchaseOrderColumnHeader.OPS_NOTE,
      column: (row: PurchaseOrder) => (
        <div className="flex justify-center">
          <EditOpsNote<PurchaseOrder>
            id={row.id}
            timestamp={undefined}
            opsNote={row.additionalInfo?.operationsNote}
            onUpdateOpsNote={onUpdateOpsNote}
          />
        </div>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.INVOICE_AT,
      column: (row: PurchaseOrder) => (row.invoicedAt ? DD_MM_YYYY.formatDate(new Date(row.invoicedAt)) : undefined),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.LAST_UPDATE,
      column: (row: PurchaseOrder) => (row.updatedAt ? DD_MM_YYYY.formatDate(new Date(row.updatedAt)) : undefined),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.CREATION_DATE,
      column: (row: PurchaseOrder) => (row.createdAt ? DD_MM_YYYY.formatDate(new Date(row.createdAt)) : undefined),
      styles: { textAlign: "center" }
    },
    {
      header: PurchaseOrderColumnHeader.ACTIONS,
      column: (row: PurchaseOrder) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            {/* Shipment Details */}
            {!PurchaseOrderService.isPOShipmentDetailsLocked(row) &&
              !PurchaseOrderService.isChildPurchaseOrder(row) &&
              RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${ROUTES_CONFIG.updatePurchaseOrderShipmentDetails.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Truck className="h-4 w-4 text-muted-600" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Update Shipment Details</p>
                  </TooltipContent>
                </Tooltip>
              )}

            {/* Parcel Hero Timeline  */}
            {PurchaseOrderService.isPOParcelHeroEligible(row) &&
              !PurchaseOrderService.isChildPurchaseOrder(row) &&
              RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${ROUTES_CONFIG.parcelHeroTimeline.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Clock className="h-4 w-4 text-muted-600" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Parcel Hero Timeline</p>
                  </TooltipContent>
                </Tooltip>
              )}

            {/* PO File Download */}
            {PurchaseOrderService.hasPurchaseOrderPDFBeenSent(row) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-secondary-100"
                    onClick={() =>
                      downloadFile(
                        row.id,
                        row.fileType === VendorFileType.CSV ? PurchaseOrderFileType.CSV : PurchaseOrderFileType.PDF
                      )
                    }
                    disabled={isFileDownloading}
                  >
                    <FileText className="h-4 w-4 text-muted-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download PO File ({row.fileType || VendorFileType.PDF})</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* More Info */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Info className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>More Info</p>
              </TooltipContent>
            </Tooltip>

            {/* Amend */}
            {(PurchaseOrderService.isChildPOAmendable(row) ||
              (!PurchaseOrderService.isPOAmendLocked(row) && !PurchaseOrderService.isChildPurchaseOrder(row))) &&
              RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM"]) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${ROUTES_CONFIG.amendPurchaseOrder.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Edit className="h-4 w-4 text-muted-600" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Amend Purchase Order</p>
                  </TooltipContent>
                </Tooltip>
              )}

            {(row.invoiceReconciliationStatus === "MISMATCH" || row.invoiceReconciliationStatus === "NEEDS_REVIEW") &&
              RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${ROUTES_CONFIG.updatePurchaseOrderInvoice.path}?id=${row.id}`}
                      className={`inline-flex items-center justify-center h-8 w-8 p-0 rounded-md transition-colors ${
                        row.invoiceReconciliationStatus === "MISMATCH" ? "hover:bg-red-50" : "hover:bg-amber-50"
                      }`}
                    >
                      <AlertTriangle
                        className={`h-4 w-4 ${
                          row.invoiceReconciliationStatus === "MISMATCH" ? "text-red-600" : "text-amber-500"
                        }`}
                      />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    {row.invoiceReconciliationStatus === "MISMATCH" ? (
                      <p>
                        Brand invoice value doesn&apos;t match the customs invoice total
                        {row.brandInvoiceValue != null && row.totalCost != null
                          ? ` (invoice ${row.brandInvoiceCurrency ?? ""}${row.brandInvoiceValue.toFixed(2)} vs £${row.totalCost.toFixed(2)})`
                          : ""}
                      </p>
                    ) : (
                      <p>Invoice value needs manual review (couldn&apos;t read it confidently)</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              )}

            {/* Invoice */}
            {RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"]) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {row.hasInvoice ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-secondary-100 transition-colors"
                      onClick={() => downloadFile(row.id, PurchaseOrderFileType.INVOICES)}
                      disabled={row.hasInvoice && isFileDownloading}
                    >
                      <Download className="h-4 w-4 text-muted-600" />
                    </Button>
                  ) : !PurchaseOrderService.isChildPurchaseOrder(row) ? (
                    <Link
                      to={`${ROUTES_CONFIG.updatePurchaseOrderInvoice.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Receipt className="h-4 w-4 text-muted-600" />
                    </Link>
                  ) : null}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{row.hasInvoice ? "Download Invoice" : "Upload Invoice"}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Commercial Invoice */}
            {PurchaseOrderService.isCommercialInvoiceAvailable(row) && (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-secondary-100 transition-colors"
                        disabled={isFileDownloading}
                      >
                        <ReceiptText className="h-4 w-4 text-muted-600" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download Commercial Invoice</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="z-[60] min-w-[10rem] border border-secondary-200 bg-white shadow-lg">
                  <DropdownMenuItem
                    className="cursor-pointer text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100"
                    onClick={() => downloadFile(row.id, PurchaseOrderFileType.COMMERCIAL_INVOICE)}
                  >
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100"
                    onClick={() => downloadFile(row.id, PurchaseOrderFileType.COMMERCIAL_INVOICE_CSV)}
                  >
                    Download CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Create Child PO */}
            {(row.completionStatus === "Complete" || row.completionStatus === "In Progress") &&
              !PurchaseOrderService.isChildPurchaseOrder(row) &&
              !PurchaseOrderService.isActiveAntsPurchaseOrder(row) &&
              RoleHelper.isRequiredRole(userRole, ["HEALF_OPERATIONS_TEAM"]) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`${ROUTES_CONFIG.createChildPurchaseOrder.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <FilePlus className="h-4 w-4 text-muted-600" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create Child Purchase Order</p>
                  </TooltipContent>
                </Tooltip>
              )}

            {additionalActions?.(row)}
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table<PurchaseOrderColumnHeader, PurchaseOrder>
      data={purchaseOrders}
      columns={purchaseOrdersTableColumns}
      columnsToHide={columnsToHide}
    />
  );
};

export { PurchaseOrdersDashTable };
